import { ColumnSizingState, Updater } from '@tanstack/react-table';
import { LocalStorage } from '@va/util/helpers';
import { StorageKeysManager } from '@va/util/misc';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';

type UseColumnSizingParams = {
  namespace: string;
  reportBlockId: string;
  selectedView: string;
  disabled?: boolean;
};

function loadFromStorage(key: string): ColumnSizingState {
  try {
    const stored = LocalStorage.getItem(key);
    if (stored) return JSON.parse(stored);
  } catch (error) {
    console.error('Error while parsing column sizing from storage:', error);
  }
  return {};
}

function updateColumnStorage(key: string, changedId: string, newValue: number | undefined) {
  try {
    const stored = loadFromStorage(key);
    if (newValue === undefined) {
      const { [changedId]: _, ...rest } = stored;
      LocalStorage.setItem(key, JSON.stringify(rest));
    } else {
      LocalStorage.setItem(key, JSON.stringify({ ...stored, [changedId]: newValue }));
    }
  } catch (error) {
    console.error('Error while saving column sizing to storage:', error);
  }
}

export const useColumnSizing = ({
  namespace,
  reportBlockId,
  selectedView,
  disabled = false,
}: UseColumnSizingParams) => {
  const storageKey = useMemo(
    () => StorageKeysManager.getColumnSizingKey({ namespace, reportBlockId, view: selectedView }),
    [namespace, reportBlockId, selectedView],
  );

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() =>
    disabled ? {} : loadFromStorage(storageKey),
  );
  const [storedColumnSizing, setStoredColumnSizing] = useState<ColumnSizingState>(columnSizing);

  useLayoutEffect(() => {
    const stored = disabled ? {} : loadFromStorage(storageKey);
    setColumnSizing(stored);
    setStoredColumnSizing(stored);
  }, [disabled, storageKey]);

  const onColumnSizingChange = useCallback<(u: Updater<ColumnSizingState>, stateOnlyUpdate?: boolean) => void>(
    (updater, stateOnlyUpdate = false) => {
      if (disabled) return;
      setColumnSizing((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        const changedId =
          Object.keys(next).find((id) => next[id] !== prev[id]) ?? Object.keys(prev).find((id) => !(id in next));
        if (!changedId) return prev;
        if (!stateOnlyUpdate) {
          const newValue = next[changedId];
          updateColumnStorage(storageKey, changedId, newValue);
          setStoredColumnSizing((prev) => {
            if (newValue === undefined) {
              const { [changedId]: _, ...rest } = prev;
              return rest;
            }
            return { ...prev, [changedId]: newValue };
          });
        }
        return next;
      });
    },
    [disabled, storageKey],
  );
  return { columnSizing, storedColumnSizing, onColumnSizingChange };
};
